import { defineStore } from 'pinia'
import type {
  CheckoutErrorResponse,
  CheckoutFetchResponse,
  CheckoutGenerateResponseInterface,
} from '~/types/checkout/checkoutBackendTypes'
import type {
  AddressInterface,
  PaymentMethodInterface,
  PickupPointInterface,
  PromotionInterface,
} from '~/types/checkoutTypes'
import { CheckoutSteps } from '~/enums/CheckoutSteps'
import { PaymentFeeTypes } from '~/enums/PaymentFeeTypes'
import isValidResponse from '~/utils/isValidResponse'
import { useLanguageStore } from '~/store/language'
import { useCartStore } from '~/store/cart'
import { fetchRetryParameters, requestHostHeaders, type RequestHostHeadersType } from '~/composables/constants/api'
import PaymentProcessorsEnum from '~/enums/PaymentProcessorsEnum'
import { useIdentity } from './identity'

export interface ProductInterface {
  parentId: number
  parentSlug: string
  brand: string
  category: string
  currentPrice: number | null
  customItemDataProc: number
  media: string
  name: string
  price: number | null
  quantity: number
  sku: string | null
  url: string | null
  fields: {
    title: string
    value: string
  }[]
  friendlyUrls: {
    value: string
    language_id: number
  }[]
}

interface CheckoutSuccessInterface extends CheckoutErrorResponse {
  shipping_methods: {
    [index: string]: number
  }
  shipping_discounts: {
    [index: string]: number
  }
  local_delivery_couriers: LocalDeliveryCourierInterface[]
  products: ProductInterface[]
  client_addresses?: AddressInterface[]
  country: {
    id: number
    name: string
    iso: string
  }
  courier_info: {
    id: number
    parcelshop_id: string
    town: string
    type: number
  } | null
  delivery_info: {
    city: string
    first_address: string
    first_name: string
    id: number
    last_name: string
    phone_number: string
    zip: string
    country_id: number
  } | null
  email: string | null
  payment_methods: PaymentMethodInterface[] | undefined
  delivery_fee: number | undefined
  shipping_fee: number | undefined
  tier_based_shipping_discount: number | undefined
  selected_shipping_method: number | null
  local_delivery: number | null
  order_id: number
  promotion: PromotionInterface | null
  payments: CheckoutInvoicePayment[]
}

interface LocalDeliveryCourierInterface {
  local_delivery_type: number
  delivery_fee: number
  display_name: string
  media: string | null
  has_pickup_points: number
  pickup_points?: PickupPointInterface[]
}

interface BalanceInterface {
  id: number | null
  amount: number | null
}

interface PaymentInterface {
  id: number | null
  type: number | null
  index: number | null
}

interface CheckoutInterface {
  data: CheckoutSuccessInterface | null
  step: number
  slug: string | null
  shipping_method: string | null
  delivery_method: number
  parcel_shop: PickupPointInterface | null
  is_guest: boolean
  address: AddressInterface
  promotion: PromotionInterface | null
  email: string | null
  stripe_token: string | null
  balance: BalanceInterface | null
  payment: PaymentInterface | null
  balanceRemoved: boolean
}

interface CheckoutInvoicePayment {
  id: number
  invoice_id: number
  amount: number
  payment_processor_id: number
  type: number
  status: number
}

function getInitialState(): CheckoutInterface {
  return {
    data: null,
    step: CheckoutSteps.Shipping,
    slug: null,
    shipping_method: null,
    delivery_method: -1,
    parcel_shop: null,
    is_guest: true,
    address: {
      id: null,
      first_name: null,
      last_name: null,
      first_address: null,
      city: null,
      zip: null,
      phone_number: null,
      country_id: null,
    },
    promotion: null,
    email: null,
    stripe_token: null,
    balance: {
      id: null,
      amount: null,
    },
    payment: {
      id: null,
      index: null,
      type: null,
    },
    balanceRemoved: false,
  }
}

export const useCheckoutStore = defineStore('checkout', {
  state: (): CheckoutInterface => {
    return getInitialState()
  },
  getters: {
    totalItems(state): number {
      if (state.data === null || state.data.status === false) {
        return 0
      }

      if (!Array.isArray(state.data.products)) {
        return 0
      }

      return state.data.products.length
    },
    subtotalPrice(state): number {
      if (state.data === null || state.data.status === false) {
        return 0
      }

      let subtotal = 0
      if (Array.isArray(state.data.products)) {
        subtotal = state.data.products.reduce(
          (previousValue, product) => (previousValue += Number(product.currentPrice) * product.quantity),
          0
        )
      }

      return subtotal
    },
    shippingPrice(state): number {
      if (state.data === null || state.data.status === false) {
        return 0
      }

      if (state.data?.shipping_fee) {
        return (state.data.shipping_fee || 0) + this.shippingDiscount
      }

      let price = 0

      if (state.shipping_method !== null) {
        price = state.data.shipping_methods[state.shipping_method]
      }

      return (price || 0) + this.shippingDiscount
    },
    deliveryPrice(state): number {
      if (state.data?.delivery_fee) {
        return state.data.delivery_fee || 0
      }

      const couriers = state.data?.local_delivery_couriers ?? null
      if (couriers === null) {
        return 0
      }

      const local_delivery_courier = couriers.find((value, index) => {
        return index === state.delivery_method
      })

      return (local_delivery_courier ? local_delivery_courier.delivery_fee : null) || 0
    },
    shippingDiscount(state): number {
      if (state.data === null || state.data.status === false) {
        return 0
      }

      if (state.data?.tier_based_shipping_discount) {
        return state.data?.tier_based_shipping_discount || 0
      }

      let discount = 0

      if (state.shipping_method !== null) {
        discount = state.data?.shipping_discounts[state.shipping_method]
      }

      return discount || 0
    },
    shippingDiscountPercentage(): number {
      if (!this.shippingDiscount) {
        return 0
      }

      return Math.round((this.shippingDiscount / this.shippingPrice) * 100)
    },
    paymentMethodFee(state): number {
      const paymentMethods = state.data?.payment_methods ?? null

      if (paymentMethods === null) {
        return 0
      }

      let fee = 0

      if (!state.payment?.id) {
        return fee
      }

      const paymentMethod = paymentMethods.find((value) => {
        return value.id === state.payment?.id
      })

      if (paymentMethod) {
        fee = paymentMethod.fee ?? 0

        if (paymentMethod.fee_type === PaymentFeeTypes.Percentage) {
          fee = (this.finalPriceWithoutPaymentMethodFee / 100) * Number(paymentMethod.fee)
          fee = Math.round((fee + Number.EPSILON) * 100) / 100
        }
      }

      return fee
    },
    promoDiscount(state): number {
      const discount = state.promotion?.discount_amount ?? null

      if (discount === null) {
        return 0
      }

      return discount
    },
    promoType(state): number | null {
      return state.promotion?.promotion_type ?? null
    },
    balanceDiscount(state): number {
      const discount = state.balance?.amount ?? null
      if (discount === null) {
        return 0
      }

      return discount
    },
    baseFinalPriceAfterPromo(state): number {
      if (state.data === null || state.data.status === false) {
        return 0
      }

      return +(
        this.subtotalPrice +
        this.shippingPrice +
        this.deliveryPrice -
        this.shippingDiscount -
        this.promoDiscount
      ).toFixed(4)
    },
    finalPriceWithoutPaymentMethodFee(state): number {
      if (state.data === null || state.data.status === false) {
        return 0
      }

      return +(this.baseFinalPriceAfterPromo - this.balanceDiscount).toFixed(4)
    },
    finalPrice(state): number {
      if (state.data === null || state.data.status === false) {
        return 0
      }

      return +(this.finalPriceWithoutPaymentMethodFee + this.paymentMethodFee).toFixed(4)
    },
    finalPriceForAnalytics(state): number {
      if (state.data === null || state.data.status === false) {
        return 0
      }

      return +(this.baseFinalPriceAfterPromo + this.paymentMethodFee).toFixed(4)
    },
    isShippingDiscounted(): boolean {
      return this.shippingDiscount > 0
    },
    products(): ProductInterface[] | null {
      return this.data?.products || null
    },
    orderId(): number | null {
      return this.data?.order_id || null
    },
    getPaymentIndex(): number | null {
      return this.payment ? this.payment.index : null
    },
  },
  actions: {
    async fetch(slug: string | null) {
      if (slug === null) {
        const res = await $fetch<CheckoutGenerateResponseInterface>('/api/checkout/generate', {
          ...fetchRetryParameters,
          timeout: 60000,
        })
        if (res.status) {
          slug = res.slug
        }
      }

      this.setSlug(slug)

      if (slug === null) {
        return
      }

      const client = useIdentity()

      if (client.user) {
        await client.refresh()
      }

      interface formDataInterface {
        slug: string | null
        step: number | null
        language_id: number | null
        fields?: string[]
      }

      const formData: formDataInterface = {
        slug,
        step: this.step,
        language_id: useLanguageStore().getLanguageId,
      }

      if (formData.step === CheckoutSteps.Payment) {
        formData.fields = ['shipping', 'delivery', 'address', 'country', 'email']
      }

      const response = await $fetch<CheckoutFetchResponse>(`/api/checkout/fetch/${slug}`, {
        method: 'POST',
        body: formData,
        ...fetchRetryParameters,
        retry: 2,
        headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
      })

      if (!isValidResponse(response)) {
        this.setSlug(null)
      }

      this.data = response
      useCartStore().invalidateCart()

      if (this.data !== null) {
        if (this.data?.payments) {
          const appliedBalance = this.data?.payments?.find(
            (payment) => payment.payment_processor_id === PaymentProcessorsEnum.ShipzeeBalance && !!payment.type
          )

          if (appliedBalance) {
            this.setBalance({
              amount: appliedBalance.amount,
              id: appliedBalance.payment_processor_id,
            })
          }
        }

        if (this.data.selected_shipping_method !== null && this.data.shipping_methods) {
          const method = Object.keys(this.data.shipping_methods)[this.data.selected_shipping_method]
          this.shipping_method = String(method)
        }

        if (this.data.local_delivery !== null && this.data.local_delivery_couriers) {
          const index = this.data.local_delivery_couriers.findIndex(
            (courier: LocalDeliveryCourierInterface) => courier.local_delivery_type === this.data?.local_delivery
          )

          if (index >= 0) {
            const courier: LocalDeliveryCourierInterface = this.data.local_delivery_couriers[index]

            if (courier.local_delivery_type === this.data?.local_delivery) {
              this.delivery_method = Number(index)

              if (courier.has_pickup_points) {
                const parcelShop = courier.pickup_points?.find(
                  (pickupPoint) => pickupPoint.parcelshop_id === this.data?.courier_info?.parcelshop_id
                )

                if (parcelShop) {
                  this.setParcelShop(parcelShop)
                }
              }
            }
          }
        }

        if (this.data.email !== null) {
          this.email = this.data.email
        }

        if (this.data.client_addresses?.length) {
          this.data.client_addresses.reverse()
        }

        if (this.data.promotion) {
          this.applyPromotion(this.data.promotion)
        }
      }

      return true
    },
    setSlug(value: string | null) {
      this.slug = value
    },
    setShippingMethod(value: string | null) {
      this.shipping_method = value
    },
    setDeliveryMethod(value: number) {
      this.delivery_method = value
    },
    setParcelShop(value: PickupPointInterface | null) {
      this.parcel_shop = value
    },
    setStep(value: number) {
      this.step = value
    },
    applyPromotion(value: PromotionInterface) {
      this.promotion = value
    },
    removePromotion() {
      this.promotion = null
    },
    setAddress(value: AddressInterface) {
      this.address = value
    },
    setEmail(value: string | null) {
      this.email = value
    },
    setStripeToken(value: string | null) {
      this.stripe_token = value
    },
    invalidateCheckout() {
      this.$reset()
    },
    addNewAddress(value: AddressInterface) {
      if (this.data?.client_addresses !== undefined) {
        const address = this.data?.client_addresses.find((address: AddressInterface) => address.id === 0)
        if (address) {
          address.first_name = value.first_name
          address.last_name = value.last_name
          address.first_address = value.first_address
          address.city = value.city
          address.zip = value.zip
          address.phone_number = value.phone_number
          address.country_id = value.country_id
        } else {
          this.data?.client_addresses.unshift({
            id: 0,
            first_name: value.first_name,
            last_name: value.last_name,
            first_address: value.first_address,
            city: value.city,
            zip: value.zip,
            phone_number: value.phone_number,
            country_id: value.country_id,
          })
        }
      }
    },
    setBalance(balance: BalanceInterface | null) {
      this.balance = balance

      if (!balance) {
        this.removeBalance()
      }
    },
    setPayment(payment: PaymentInterface | null) {
      this.payment = payment
    },
    async removeBalance() {
      const slug = this.slug

      if (slug !== null && this.data?.payments.length && !this.balanceRemoved) {
        const res = await $fetch<{
          status: boolean
        }>('/api/checkout/remove-balance', {
          method: 'POST',
          body: {
            slug,
          },
          ...fetchRetryParameters,
          headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
        })

        if (res.status) {
          this.balanceRemoved = true
          this.balance = null
        }
      }
    },
  },
})
